import type { To } from 'react-router'

import {
  canonicalizeStudioLocalePathname,
  getStudioLanguageFromPathname,
} from './canonicalize-studio-locale-pathname'

function isExternalLocation(to: string): boolean {
  // `//host/path` is a URL, not a path, but it satisfies the `startsWith('/')`
  // test below — so without it here the host becomes a path segment and the
  // reader is sent to `/de/host/path` on this origin.
  return /^(https?:|mailto:|tel:|\/\/)/i.test(to)
}

/**
 * Rewrites a string `to` value so its pathname locale prefix matches the given
 * language. Preserves query string and hash. External URLs are unchanged.
 *
 * Empty segments are dropped as a side effect of rebuilding the pathname, so
 * duplicate and trailing slashes are normalized (`/a//b/` becomes `/de/a/b`).
 * That is a safe canonicalization for react-router, which matches either form to
 * the same route.
 */
export function canonicalizeStudioLocaleTo(to: string, language: string): string {
  if (isExternalLocation(to)) {
    return to
  }

  const hashIndex = to.indexOf('#')
  const hash = hashIndex >= 0 ? to.slice(hashIndex) : ''
  const withoutHash = hashIndex >= 0 ? to.slice(0, hashIndex) : to

  const searchIndex = withoutHash.indexOf('?')
  const search = searchIndex >= 0 ? withoutHash.slice(searchIndex) : ''
  const pathnamePart = searchIndex >= 0 ? withoutHash.slice(0, searchIndex) : withoutHash

  if (!pathnamePart.startsWith('/')) {
    return to
  }

  const canonicalPathname = getStudioLanguageFromPathname(pathnamePart)
    ? pathnamePart
    : canonicalizeStudioLocalePathname(pathnamePart, language)

  return `${canonicalPathname}${search}${hash}`
}

/** Canonicalizes react-router `To` values used by `<Link to={...} />`. */
export function canonicalizeStudioLocaleLinkTo(to: To, language: string): To {
  if (typeof to === 'string') {
    return canonicalizeStudioLocaleTo(to, language)
  }

  // Nullish falls through to the return below, because react-router tolerates it
  // — `resolveTo` spreads `to` into an empty object and resolves against the
  // current location. This helper runs in front of every link in three apps, so
  // it has to be equally total: reading `to.pathname` crashed the localized video
  // lightbox, whose arrow controls render a hidden `<Link>` with no target when
  // there is nothing adjacent to navigate to.
  //
  // A relative pathname also falls through. It resolves against the current
  // route, so prefixing it would turn `{ pathname: 'details' }` into an absolute
  // `/de/details` and send the reader somewhere else entirely — the same guard
  // the string branch applies.
  if (to?.pathname?.startsWith('/')) {
    return {
      ...to,
      pathname: getStudioLanguageFromPathname(to.pathname)
        ? to.pathname
        : canonicalizeStudioLocalePathname(to.pathname, language),
    }
  }

  return to
}
